/**
 * Merchant Pro V1 PR-2 — Reservations List page (`/owner/reservations`).
 *
 * Filterable, paginated table backed by the existing
 * `GET /api/owner/reservations` endpoint. Filter chips: Today /
 * Tomorrow / This Week / Custom. Status pills reuse <StatusPill>
 * primitive. Row click → /owner/reservations/{id} (Detail page).
 *
 * Operator-locked discipline:
 *   • NO checkboxes / NO bulk actions (operator decision B=Defer).
 *   • NO row-level no-show / complete / notes actions.
 *   • Reuses existing <StatusPill>, <PageHeader>, <SectionCard>,
 *     <FullSpinner>, <QueryError> primitives — zero parallel
 *     pill / row / page components built.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Search, Crown } from 'lucide-react';

import { useAuthStore } from '@/lib/auth';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';

import {
  PageHeader,
  SectionCard,
  StatusPill,
  FullSpinner,
  QueryError,
  EmptyState,
} from '@/components/ui_primitives/Misc';
import { classes } from '@/lib/utils';
import ChairGridTableCard from '@/components/owner/ChairGridTableCard';

/* E2 PR4d-3 — Map reservation status → derived table state so the inline
 * chair-grid thumbnail renders with the right color. Reservation-status
 * is the closest signal we have at the list level without an additional
 * floor-plan-state fetch. Order matches the §8.2 palette.
 */
const RESERVATION_STATUS_TO_TABLE_STATE = {
  confirmed:               'reserved',
  checked_in:              'checked_in',
  completed:               'available',
  no_show:                 'available',
  cancelled_by_owner:      'available',
  cancelled_by_customer:   'available',
  cancelled_by_cashier:    'available',
  cancelled_by_system:     'available',
  rescheduled:             'available',
  requested:               'available',
  held_pending:            'available',
  waitlist_promotion_pending: 'available',
};

const PAGE_SIZE = 20;

/* StatusPill variant mapping — reuse the existing 5 variants. */
const STATUS_PILL_VARIANT = {
  requested: 'amber',
  held_pending: 'amber',
  waitlist_promotion_pending: 'amber',
  confirmed: 'gold',
  checked_in: 'blue',
  completed: 'green',
  cancelled_by_owner: 'red',
  cancelled_by_customer: 'red',
  cancelled_by_cashier: 'red',
  cancelled_by_system: 'red',
  no_show: 'red',
  rescheduled: 'slate',
  expired: 'slate',
};

function reservationStatusPill(status, testID) {
  const variant = STATUS_PILL_VARIANT[status] || 'slate';
  const label = (status || '').replace(/_/g, ' ');
  return (
    <StatusPill variant={variant} testID={testID}>
      {label}
    </StatusPill>
  );
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/**
 * Filter chip presets. Reuses the same chip pattern as RangeTabs.
 * Each preset resolves to a [date_from, date_to] ISO pair at click time.
 */
const FILTER_CHIPS = [
  { value: 'today',     label: 'Today' },
  { value: 'tomorrow',  label: 'Tomorrow' },
  { value: 'this_week', label: 'This Week' },
  { value: 'custom',    label: 'Custom' },
];

function resolveDateRange(filterValue, customRange) {
  const today = startOfDay(new Date());
  if (filterValue === 'today') {
    return {
      date_from: today.toISOString(),
      date_to: new Date(today.getTime() + 86_400_000).toISOString(),
    };
  }
  if (filterValue === 'tomorrow') {
    const tomorrow = new Date(today.getTime() + 86_400_000);
    return {
      date_from: tomorrow.toISOString(),
      date_to: new Date(tomorrow.getTime() + 86_400_000).toISOString(),
    };
  }
  if (filterValue === 'this_week') {
    return {
      date_from: today.toISOString(),
      date_to: new Date(today.getTime() + 7 * 86_400_000).toISOString(),
    };
  }
  // custom
  return {
    date_from: customRange.from
      ? new Date(customRange.from).toISOString()
      : undefined,
    date_to: customRange.to
      ? new Date(customRange.to).toISOString()
      : undefined,
  };
}

function formatSlot(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch {
    return iso;
  }
}

export default function ReservationsListPage() {
  const token = useAuthStore((s) => s.token);

  const [filter, setFilter] = useState('today');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);

  const range = useMemo(
    () => resolveDateRange(filter, customRange),
    [filter, customRange],
  );

  const params = useMemo(() => ({
    ...range,
    status: statusFilter || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }), [range, statusFilter, page]);

  const listQ = useQuery({
    queryKey: qk.ownerReservations(params),
    queryFn: () => api.ownerReservations(token, params),
    keepPreviousData: true,
    enabled: !!token,
    staleTime: 15_000,
  });

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < total;

  return (
    <div className="space-y-6" data-testid="owner-reservations-list-page">
      <PageHeader
        kicker="Reservations"
        title="Reservations"
        subtitle="All reservations across your brand. Filter by date or status, then open a row to act on it."
      />

      {/* Filter chips — reuses the same pattern as BrandAnalyticsPanel's RangeTabs */}
      <SectionCard testID="reservations-filter-card" padded={false} className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div
            data-testid="reservations-date-chips"
            className="inline-flex p-1 bg-black/40 border border-white/8 rounded-xl"
          >
            {FILTER_CHIPS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => {
                  setFilter(c.value);
                  setPage(0);
                }}
                data-testid={`reservations-chip-${c.value}`}
                className={classes(
                  'px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors',
                  filter === c.value
                    ? 'bg-gold/20 text-gold border border-gold/30'
                    : 'text-white/60 hover:text-white border border-transparent',
                )}
              >
                {c.label}
              </button>
            ))}
          </div>

          {filter === 'custom' ? (
            <div className="flex items-center gap-2 text-xs text-white/70">
              <input
                type="date"
                value={customRange.from}
                onChange={(e) => {
                  setCustomRange((r) => ({ ...r, from: e.target.value }));
                  setPage(0);
                }}
                data-testid="reservations-custom-from"
                className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-white"
              />
              <span className="text-white/40">→</span>
              <input
                type="date"
                value={customRange.to}
                onChange={(e) => {
                  setCustomRange((r) => ({ ...r, to: e.target.value }));
                  setPage(0);
                }}
                data-testid="reservations-custom-to"
                className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-white"
              />
            </div>
          ) : null}

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            <Search size={14} className="text-white/40" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              data-testid="reservations-status-filter"
              className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white/80 focus:outline-none focus:border-gold/40"
            >
              <option value="">All statuses</option>
              <option value="requested">Requested</option>
              <option value="confirmed">Confirmed</option>
              <option value="checked_in">Checked-in</option>
              <option value="completed">Completed</option>
              <option value="cancelled_by_owner">Cancelled (owner)</option>
              <option value="cancelled_by_customer">Cancelled (customer)</option>
              <option value="no_show">No-show</option>
            </select>
          </div>
        </div>
      </SectionCard>

      {/* Table */}
      <SectionCard testID="reservations-table-card" padded={false}>
        {listQ.isLoading ? (
          <FullSpinner />
        ) : listQ.isError ? (
          <div className="p-6">
            <QueryError error={listQ.error} onRetry={listQ.refetch} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="No reservations found"
            subtitle="Try widening the date range or changing the status filter."
            testID="reservations-empty"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="reservations-table">
              <thead>
                <tr className="text-[11px] uppercase tracking-widest font-semibold text-white/40 border-b border-white/10">
                  <th className="text-left px-5 py-3 font-semibold w-12">Table</th>
                  <th className="text-left px-5 py-3 font-semibold">Customer</th>
                  <th className="text-left px-3 py-3 font-semibold">Slot</th>
                  <th className="text-left px-3 py-3 font-semibold">Party</th>
                  <th className="text-left px-3 py-3 font-semibold">Code</th>
                  <th className="text-left px-3 py-3 font-semibold">Status</th>
                  <th className="text-right px-5 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const tier = row.membership?.tier || row.customer?.tier || null;
                  const isConfirmed = row.status === 'confirmed';
                  /* E2 PR4d-3 — Inline chair-grid thumbnail for the bound
                   * table. The list payload now carries `table_id` and
                   * `room_id` post-E2 Step 0 (P2-LIST-STALE-AFTER-MOVE-A
                   * fix); we synthesise a minimal table-shaped object so
                   * the canonical `<ChairGridTableCard>` xs variant can
                   * render the 32-px thumbnail. Real `state` derives from
                   * reservation status (closest signal available without
                   * a separate floor-plan-state fetch). */
                  const thumbTable = row.table_id ? {
                    id:       row.table_id,
                    name:     '',
                    capacity: row.party_size ?? 1,
                    state:    RESERVATION_STATUS_TO_TABLE_STATE[row.status] ?? 'available',
                  } : null;
                  return (
                    <tr
                      key={row.id}
                      data-testid={`reservation-row-${row.id}`}
                      data-status={row.status}
                      className={classes(
                        'border-b border-white/5 last:border-b-0 hover:bg-white/[0.03] transition-colors',
                        isConfirmed && 'shadow-[inset_2px_0_0_0_rgba(212,175,55,0.6)]',
                      )}
                    >
                      <td className="px-5 py-2.5">
                        <div
                          data-testid={`reservation-table-thumb-${row.id}`}
                          className="w-8 h-8 grid place-items-center"
                        >
                          {thumbTable ? (
                            <div className="w-8 h-8">
                              <ChairGridTableCard
                                table={thumbTable}
                                size="xs"
                                showStatePill={false}
                                showLegendIcon={false}
                                testIdPrefix={`reservation-thumb-${row.id}`}
                              />
                            </div>
                          ) : (
                            <div
                              data-testid={`reservation-table-thumb-${row.id}-placeholder`}
                              className="w-8 h-8 rounded-xl border border-white/20 border-dashed bg-white/[0.02]"
                              aria-label="Unassigned"
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="text-white font-medium truncate max-w-[180px]">
                            {row.customer?.name || '—'}
                          </div>
                          {tier ? (
                            <span
                              data-testid={`reservation-tier-${row.id}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gold/15 border border-gold/30 text-gold text-[9px] uppercase tracking-wider font-bold"
                            >
                              <Crown size={9} />
                              {tier}
                            </span>
                          ) : null}
                        </div>
                        <div className="text-[11px] text-white/40 font-mono mt-0.5">
                          {row.customer?.phone || ''}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-white/80 whitespace-nowrap">
                        {formatSlot(row.slot_start)}
                      </td>
                      <td className="px-3 py-2.5 text-white/80 tabular-nums">
                        {row.party_size}
                      </td>
                      <td className="px-3 py-2.5 text-gold/85 font-mono text-xs">
                        {row.booking_code || '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        {reservationStatusPill(row.status, `status-${row.id}`)}
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        <Link
                          to={`/owner/reservations/${row.id}`}
                          data-testid={`reservation-open-${row.id}`}
                          className="text-xs font-semibold text-gold/90 hover:text-gold whitespace-nowrap"
                        >
                          Open →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {items.length > 0 ? (
          <div className="flex items-center justify-between px-5 py-3 border-t border-white/5">
            <div className="text-xs text-white/55" data-testid="reservations-pagination-info">
              Showing {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + items.length} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={!hasPrev || listQ.isFetching}
                data-testid="reservations-prev-page"
                className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 text-white/70 hover:bg-gold/10 hover:border-gold/30 hover:text-gold grid place-items-center disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasNext || listQ.isFetching}
                data-testid="reservations-next-page"
                className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 text-white/70 hover:bg-gold/10 hover:border-gold/30 hover:text-gold grid place-items-center disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        ) : null}
      </SectionCard>
    </div>
  );
}
